import {
  AppMetadataProps,
  EventItem,
  GetSeriesById,
  QueryMetadata,
  SeriesConfig,
  TimeSeriesTotals,
  TooltipMode,
  TooltipSort,
} from '@/core';
import { TooltipContextMenuOptions } from '@/panels-plugin';
import { CursorData } from '../PanelTooltip';

/**
 * Min width is dependent on how the tooltip is being used, see PR #48467.
 * Medium leaves enough room for default tooltip header but is not too large when series names are short.
 * Large is for cases with an end date like the event grouping tooltip or histogram buckets,
 * where we do not want the 'Click chart to pin' text to overlap with verbose dates.
 */
export const TOOLTIP_MIN_WIDTH = {
  medium: 260,
  large: 360,
};

export const TOOLTIP_MAX_WIDTH = 650;
export const TOOLTIP_NEARBY_SERIES_MAX_HEIGHT = 375;
export const TOOLTIP_HEIGHT_INCREASE_INCREMENT = 75;
export const TOOLTIP_LABELS_MAX_WIDTH = TOOLTIP_MAX_WIDTH - 150;

export const NEARBY_SERIES_DESCRIPTION = 'nearby series showing in tooltip';
export const EMPHASIZED_SERIES_DESCRIPTION = 'emphasized series showing as bold in tooltip';

export const TOOLTIP_BG_COLOR_FALLBACK = '#2E313E';

export const EVENT_ICON_SIZE = 16;

export const defaultCursorData = {
  coords: {
    plotCanvas: {
      x: 0,
      y: 0,
    },
    target: null,
  },
  chartWidth: 0,
};

export const emptyTooltipData = {
  cursor: defaultCursorData,
  focusedSeries: null,
};

export interface TooltipData {
  focusedSeries: TooltipSeriesArray | null;
  cursor: CursorData;
}

export interface TooltipSeriesInfo {
  seriesId: string;
  seriesIndex: number | null;
  dataIndex: number | null;
  seriesName: string;
  date: number;
  markerColor: string;
  x: number;
  y: number;
  formattedY?: string;
  shouldEmphasizeText: boolean;
  distanceFromCursorY?: number;
  event?: EventItem;
  config?: SeriesConfig;
  markerType?: 'solid' | 'striped';
}

export type TooltipSeriesArray = TooltipSeriesInfo[];

export type ExcludeEmptyResults = 'all' | 'none';

export type ShowEndTimeArgs = {
  /**
   * Controls whether to display the end time in the tooltip and how to calculate it.
   * - 'timeRange': Use the time picker time range end date.
   * - 'stepMs': Calculate end time using step size
   */
  strategy: 'timeRange' | 'stepMs';
};

export type TooltipConfig = {
  mode: TooltipMode;
  sort?: TooltipSort;
  maxNearbySeries?: number;
  enableTooltipPreciseValues?: boolean;
  wrapSeriesName?: boolean;
  hidden?: boolean;
  enablePinning?: boolean;
  excludeEmptyResults?: ExcludeEmptyResults;
  showEndTime?: ShowEndTimeArgs;
  showTotal?: boolean;
  totalValuePerTimestamp?: TimeSeriesTotals;
  links?: TooltipLink[];
  rightYAxisIndex?: number;
  getSeriesConfig?: (seriesId: string) => SeriesConfig;
  getSeriesById?: GetSeriesById;
  hasLogScale?: boolean;
  /**
   * When true, use pixel-based proximity for nearby series (required for stacked charts
   * since raw y values don't match cursor position in data space).
   */
  isStacked?: boolean;
  /**
   * Tooltip context menu passed via chart props
   */
  tooltipContextMenuProps?: TooltipContextMenuOptions;
  /**
   * Tooltip context menu provided by the data query plugin
   */
  tooltipContextMenuData?: Array<QueryMetadata['tooltipContextMenu']>;
  appMetadata?: AppMetadataProps;
  panelKey?: string;
  /**
   * Common labels across all series (for "Unique labels" mode in Prometheus)
   */
  commonLabels?: Record<string, string>;
  /**
   * Whether to expand common labels section by default, depends on the number of common labels.
   */
  expandCommonLabels?: boolean;
};

/**
 * Consistent number of nearby series to always show in tooltip to reduce jitter as cursor moves.
 * Dynamic logic is in useTooltipConfig to adjust based on optimized mode limit and whether common labels are present.
 */
export const DEFAULT_MAX_NEARBY_SERIES = 10;

/**
 * Max nearby series when common labels are populated.
 * Important since there is room for fewer series in the tooltip (without needing to pin and scroll).
 */
export const COMMON_LABELS_POPULATED_MAX_NEARBY_SERIES = 5;

/**
 * Number of series to show in tooltip before extracting the emphasized series to render it separately in a static section.
 * Allows the bold series from being pushed down and not visible.
 */
export const SEPARATE_EMPHASIZED_SERIES_LIMIT = 8;

/**
 * Threshold for auto-expanding common labels section.
 * If common labels count is below this, the section is expanded by default.
 * If at or above this, the section is collapsed by default to reduce tooltip clutter.
 */
export const COMMON_LABELS_AUTO_COLLAPSE_THRESHOLD = 6;

export const DEFAULT_TOOLTIP_CONFIG: TooltipConfig = {
  mode: 'nearby',
  maxNearbySeries: DEFAULT_MAX_NEARBY_SERIES,
  enablePinning: true,
  enableTooltipPreciseValues: false,
  showEndTime: undefined, // Disabled by default
  showTotal: false,
};

export const MIN_THROTTLE_MS = 10; // Fastest update rate for tooltip positioning
export const MAX_THROTTLE_MS = 300; // Upper limit for throttle time in milliseconds
export const THROTTLE_INCREASE_SENSITIVITY = 80; // Increase and the wait time will rise more sharply

export const EMPTY_TOOLTIP_DATA: TooltipSeriesArray = [];

// TODO: can this be consolidated with TooltipSeriesInfo
export type SeriesInfoContext = {
  seriesName: string;
  seriesIndex: number;
  seriesId: string;
  y: number;
  x: number;
  formattedY?: string;
  markerColor: string;
  totalSeries: number;
  emphasizeText?: boolean;
  wrapSeriesName?: boolean;
};

export type TooltipLink = {
  title: string;
  buildUrl: (series: SeriesInfoContext) => string;
};
